import bindAllOfThis from 'src/utils/BindThisHelper';
import ImageApi from 'src/utils/ImageHelper';
import { PhaserColorWheel } from '../../shared/ColorWheel';
import { EndGameScene } from 'src/games/ShipPainting/scenes/EndGameScene';

// Paintbrush helper class to hold drawing attributes
class PaintBrush {
    public thickness: number;
    public color: number;
    public alpha: number;

    // default color yellow, thickness 5
    constructor() {
        this.thickness = 12;
        this.color = 0x223e93;
        this.alpha = 1;
    }
}

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    // Easel variables
    private easelControls: Phaser.Geom.Rectangle;
    // Paintbrush holds draw stroke attributes
    private paintBrush: PaintBrush = new PaintBrush();
    // Arrays to hold history and colors
    private lineHistory: Phaser.Geom.Point[];
    private drawing: boolean;
    private finishButton: Phaser.GameObjects.Sprite;
    private ship: Phaser.GameObjects.Sprite;
    private stickers: Phaser.GameObjects.Image[];
    private shipTexture: Phaser.GameObjects.RenderTexture;
    private shipGraphic: Phaser.GameObjects.Graphics;
    private shipArea: Phaser.Geom.Polygon;
    private div: HTMLDivElement = document.createElement('div');
    private currentImage: Phaser.GameObjects.Image;
    private paste: Phaser.GameObjects.Image;
    private pastedImages: Phaser.GameObjects.Image[];
    private paintGunCursor: string = 'url(' + require("/src/assets/qci/sprites/cursors/paint_gun.cur") + '), pointer';
    private drawingMode: boolean = false;

    public static CROPPED_IMAGE: string = null;

    constructor() {
        super({
            key: "MainScene"
        });
    }

    preload(): void {
        this.load.image("background", require("src/assets/qci/backgrounds/2a_background.png"));
        this.load.image("art-board", require("src/assets/qci/sprites/gameShipPainting/art_board.png"));
        this.load.spritesheet('ship', require("/src/assets/qci/sprites/gameShipPainting/ship.png"), { frameWidth: 990, frameHeight: 550 });
        this.load.spritesheet('button', require("/src/assets/qci/sprites/gameShipPainting/button.png"), { frameWidth: 90, frameHeight: 90 });
        this.load.spritesheet('stickers', require("/src/assets/qci/sprites/gameShipPainting/stickers.png"), { frameWidth: 100, frameHeight: 100 });

        PhaserColorWheel.preload(this);
    }

    create(): void {
        bindAllOfThis(this, MainScene.prototype);
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.input.setDefaultCursor(this.paintGunCursor);

        this.buildBackgroundContent();

        this.stickers = [];
        this.lineHistory = [];
        this.pastedImages = [];
        this.drawing = false;
        this.drawingMode = true;
        document.body.appendChild(this.div);

        // Register the click events
        this.registerEvents();
    }

    buildBackgroundContent() {
        let background = this.add.image(0, 0, "background");
        background.setOrigin(0);

        let artBoard = this.add.image(0, 0, "art-board");
        artBoard.setOrigin(0);
        artBoard.setY(this.gameHeight - artBoard.height);
        this.easelControls = artBoard.getBounds();

        this.ship = this.add.sprite(this.gameWidth / 2, 275, "ship", 0);
        this.ship.setInteractive();

        // Animate the lights on the ship
        this.anims.create({
            key: "shipLights",
            frames: this.anims.generateFrameNumbers("ship", { start: 0, end: 1 }),
            frameRate: 3,
            repeat: -1
        });
        this.ship.anims.play("shipLights");

        this.buildEasel();
        this.buildButton();
        this.createStickers();
        this.buildColorWheel();
    }

    buildEasel(): void {
        var json = require("../objects/Ship.json");
        var points = json["polygon"];
        var topLeft = this.ship.getTopLeft();
        points.forEach((p) => {
            p.x += topLeft.x;
            p.y += this.ship.y - 5;
        });

        this.shipArea = new Phaser.Geom.Polygon(points);

        this.ship.on('pointerdown', (pointer) => {
            this.pasteImage(pointer);
        });

        this.shipTexture = this.add.renderTexture(0, 0, this.gameWidth, this.gameHeight);
        this.shipTexture.setOrigin(0);
        this.shipTexture.setDepth(0);

        this.shipGraphic = this.add.graphics();
        this.shipGraphic.setDepth(0);
    }

    private canPaste(): boolean {
        // Don't even have a sticker right now
        if (!this.currentImage.visible) {
            return false;
        }

        return this.inShipArea();
    }

    inShipArea(): boolean {
        var bounds = this.currentImage.getBounds();
        var center = this.currentImage.getCenter();
        bounds.width *= 0.1;
        bounds.height *= 0.1;
        bounds.centerX = center.x;
        bounds.centerY = center.y;

        if (this.shipArea.contains(center.x, center.y)) {
            return true;
        }

        // Check if it contains any of the 4 corners (polygons don't have an intersects method)
        if (this.shipArea.contains(bounds.left, bounds.bottom) ||
            this.shipArea.contains(bounds.right, bounds.bottom) ||
            this.shipArea.contains(bounds.left, bounds.top) ||
            this.shipArea.contains(bounds.right, bounds.top)) {
            return true;
        }

        return false;
    }

    pasteImage(pointer) {
        if (this.canPaste()) {
            this.paste = this.add.image(pointer.x, pointer.y, "stickers", this.currentImage.frame.name);
            this.paste.setInteractive();
            this.input.setDraggable(this.paste);
            this.pastedImages.push(this.paste);
        }
    }

    buildButton(): void {
        this.finishButton = this.add.sprite(0, 0, "button", 0);
        this.finishButton.setPosition(this.gameWidth - 80, this.gameHeight - this.finishButton.height);
        this.finishButton.setInteractive({ useHandCursor: true });

        this.finishButton.on('pointerover', () => {
            this.finishButton.setFrame(1);
        });
        this.finishButton.on('pointerout', () => {
            this.finishButton.setFrame(0);
        });

        this.finishButton.on("pointerdown", () => {
            this.finishButton.setFrame(1);
            this.drawingMode = false;
            this.currentImage.setVisible(false);
            // Screenshot for later
            this.registry.parent.renderer.snapshot((image: HTMLImageElement) => {
                image.onload = () => {
                    var croppedImage = ImageApi.CropImageToDataURI(image, 0, 0, this.sys.canvas.width - 300, this.sys.canvas.height);
                    console.warn("Screenshot!", croppedImage); // JB - Remove this console.warn
                    this.endGame(croppedImage);
                };
            });
        });
    }

    buildColorWheel(): void {
        var colorWheel = new PhaserColorWheel(this, 20, this.gameHeight - 190, 175, 175);
        colorWheel.on("colorChange", (color: Phaser.Display.Color) => {
            this.drawingMode = true;
            this.paintBrush.color = color.color;
            this.refreshPaintBrush();
            this.input.setDefaultCursor(this.paintGunCursor);
            this.currentImage.setVisible(false);
        });
        colorWheel.setDepth(2);
    }

    createStickers() {
        this.currentImage = this.add.image(0, 0, "stickers");
        this.currentImage.setDepth(1);
        this.currentImage.setVisible(false);
        this.stickers = [];

        for (var frame = 0; frame < 15; frame++) {
            this.createSticker(frame);
        }

        var row1 = this.stickers.slice(0, 8);
        var row2 = this.stickers.slice(8);

        Phaser.Actions.GridAlign(row1, {
            x: 300,
            y: this.gameHeight - 150,
            width: 8,
            height: 1,
            cellWidth: 115,
            cellHeight: 100,
            position: Phaser.Display.Align.CENTER,
        });
        Phaser.Actions.GridAlign(row2, {
            x: 375,
            y: this.gameHeight - 50,
            width: 7,
            height: 1,
            cellWidth: 115,
            cellHeight: 100,
            position: Phaser.Display.Align.CENTER,
        });
    }

    createSticker(frame) {
        var sticker = this.add.image(0, 0, "stickers", frame);
        sticker.setDepth(2);
        sticker.setInteractive();
        sticker.on("pointerdown", () => {
            this.drawingMode = false;
            this.drawing = false;
            this.currentImage.setFrame(sticker.frame.name);
            this.currentImage.setVisible(true);
            this.input.setDefaultCursor('none');
        });
        this.stickers.push(sticker);
    }

    refreshPaintBrush() {
        this.shipGraphic.lineStyle(this.paintBrush.thickness, this.paintBrush.color, this.paintBrush.alpha);
    }

    registerEvents(): void {
        this.input.on('pointerdown', this.pointerDown);
        this.input.on('pointerup', this.pointerUp);
        this.input.on('pointermove', this.pointerMove);
        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image) => {
            if (!this.drawingMode) {
                this.children.bringToTop(gameObject);
                this.drawingMode = false;
                gameObject.x = pointer.downX;
                gameObject.y = pointer.downY;
            }
        });

        this.input.on('drag', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image, dragX: number, dragY: number) => {
            if (this.drawingMode) {
                return;
            }
            gameObject.x = pointer.x;
            gameObject.y = pointer.y;
        });

        this.input.on('dragend', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image) => {
            // If we're drawing, no need to do anything
            if (this.drawing || this.drawingMode) {
                return;
            }

            if (!this.inShipArea()) {
                gameObject.destroy();
            }
            else {
                this.children.moveDown(gameObject);
            }
        });
    }

    canDraw(pointer: Phaser.Input.Pointer) {
        if (this.shipArea.contains(pointer.x, pointer.y)) {
            return true;
        }

        return false;
    }

    pointerDown(pointer: Phaser.Input.Pointer): void {
        if (this.drawingMode && this.canDraw(pointer)) {
            this.drawing = true;
        }

        this.lineHistory = [];
    }

    pointerUp(pointer: Phaser.Input.Pointer): void {
        this.drawing = false;
        // Capture the graphics as a texture
        this.shipGraphic.generateTexture("background-drawn", this.sys.canvas.width, this.sys.canvas.height);
        this.shipGraphic.clear();

        let tempImage: Phaser.GameObjects.Image = this.add.image(0, 0, "background-drawn");
        this.shipTexture.save();
        this.shipTexture.draw(tempImage.texture, tempImage.frame, 0, 0);
        this.shipTexture.restore();

        tempImage.destroy();
    }

    pointerMove(pointer: Phaser.Input.Pointer): void {
        if (this.drawing && this.canDraw(pointer)) {
            this.shipGraphic.clear();
            this.refreshPaintBrush();
            this.lineHistory.push(new Phaser.Geom.Point(pointer.x, pointer.y));
            // Do the drawing
            this.shipGraphic.strokePoints(this.lineHistory);
        }

        this.currentImage.x = pointer.x;
        this.currentImage.y = pointer.y;

        if (this.drawingMode) {
            this.currentImage.setVisible(false);
            this.input.setDefaultCursor(this.paintGunCursor);
        }
        else {
            this.currentImage.setVisible(true);
            this.input.setDefaultCursor("none");
            if (this.isMovingExistingImage(pointer)) {
                this.currentImage.setVisible(false);
                this.input.setDefaultCursor('-webkit-grabbing');
            }
        }

        if (this.easelControls.contains(this.input.activePointer.x, this.input.activePointer.y)) {
            this.currentImage.setVisible(false);
            this.input.setDefaultCursor('pointer');
        }
    }

    isMovingExistingImage(pointer: Phaser.Input.Pointer): boolean {
        for (var i of this.pastedImages) {
            if (i.getBounds().contains(pointer.x, pointer.y)) {
                return true;
            }
        }
        return false;
    }

    endGame(image: string): void {
        MainScene.CROPPED_IMAGE = image;

        this.input.setDefaultCursor("default");
        this.input.removeAllListeners();
        this.scene.add("EndGameScene", EndGameScene, true);
        this.scene.remove(this);
    }

    drawPolygonShipArea() {
        // Draw polygon and set it up
        var graphics = this.add.graphics();
        graphics.lineStyle(2, 0x000000);
        graphics.beginPath();
        graphics.moveTo(this.shipArea.points[0].x, this.shipArea.points[0].y);
        for (var i = 1; i < this.shipArea.points.length; i++) {
            graphics.lineTo(this.shipArea.points[i].x, this.shipArea.points[i].y);
        }
        graphics.closePath();
        graphics.strokePath();
    }
}



// WEBPACK FOOTER //
// ./src/games/ShipPainting/scenes/MainScene.ts